import {
  Avatar,
  Box,
  Button,
  FormControl,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { CSSProperties, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../components/modal/CustomModal";
import {
  Control,
  Controller,
  RegisterOptions,
  useForm,
} from "react-hook-form";
import { InputReactHookForm } from "../../../components/input/InputReactHookForm";
import Grid2 from "@mui/material/Unstable_Grid2";
import CustomDatePicker from "../../../components/input/CustomDatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useAuth } from "../../../contexts/UserContext";
import { OrganizationContext } from "../../../contexts/OrganizationContext";
import alertService from "../../../components/alert/alertService";
import { ApiError } from "../../../api/ApiError";
import imageCompression from "browser-image-compression";

import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import GridOnIcon from "@mui/icons-material/GridOn";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileUtils from "../../../utils/fileUtils";
import LabelInput from "../../../components/input/LabelInput";
import {
  MAX_UPLOAD_FILE_SIZE_IMPORT,
  NUMBER_REGEX,
} from "../../../constants/form";
import {
  GenerateTGOReportForm,
  ReportFormat,
  ReportService,
  downloadFiles,
} from "../../../service/report.service";
import { SelectCheckmarks } from "../../../components/input/SelectCheckmarks";
import { useReport } from "../../../contexts/ReportContext";
import { common, input, report } from "../../../i18n";
import { MultipleAutoComplete } from "../../../components/input/MutipleAutoComplete";
import ArrayUtils from "../../../utils/arrayUtils";

export const TgoGenerateReport = () => {
  const { getReport } = useReport();

  const context = useContext(OrganizationContext);

  const { t } = useTranslation([common, input, report], {
    nsMode: "fallback",
  });

  const [open, setOpen] = useState<boolean>(false);

  const {
    setValue,
    getValues,
    control,
    reset,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<GenerateTGOReportForm>({
    shouldFocusError: true,
    mode: "onChange",
  });

  const logo = watch("logo");

  const map = watch("map");

  const chart = watch("chart");

  const flowchart = watch("flowchart");

  const formats = watch("formats");

  const isWordSelected = (formats ?? []).includes("word");

  const startDate = watch("startDate");

  const startDateBaseYear = watch("startDateBaseYear");

  const compressedImage = async (file: File) => {
    try {
      const options = {
        maxSizeMB: 5,
        useWebWorker: true,
        alwaysKeepResolution: true,
      };
      const compressedFile = await imageCompression(file, options);
      const imageDataUrl = await imageCompression.getDataUrlFromFile(
        compressedFile,
      );

      return imageDataUrl;
    } catch (error) {
      if (error instanceof ApiError) return;
      alertService.error("Failed to resize image");
    }

    return undefined;
  };

  const handleLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (file) {
      if (file.size > MAX_UPLOAD_FILE_SIZE_IMPORT) {
        alertService.info(
          t("Maximum-file-size-exceeded", {
            size: (MAX_UPLOAD_FILE_SIZE_IMPORT / 1e6).toFixed(0),
          }),
        );

        event.target.value = "";

        return;
      }

      const result = await compressedImage(file);
      setValue("logo", result ?? "");

      event.target.value = "";
    }
  };

  const handleMapChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (file) {
      const result = await compressedImage(file);
      setValue("map", result ?? "");
    }
  };

  const handleChartChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (file) {
      const result = await compressedImage(file);
      setValue("chart", result ?? "");
    }
  };

  const handleFlowchartChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (file) {
      const result = await compressedImage(file);
      setValue("flowchart", result ?? "");
    }
  };

  const setDefaultDate = (event: any) => {
    const date: Dayjs = event.target.value;

    setValue("endDate", date.add(11, "M"));
  };

  const onSubmit = async (values: GenerateTGOReportForm) => {
    let result;
    try {
      result = await ReportService.generateTGOReport(values);
    } catch {
      // error alert already shown by service
      setOpen(false);
      return;
    }

    if (result) {
      downloadFiles(result.files);
      getReport();
    }
    setOpen(false);
  };

  useEffect(() => {
    if (errors.reportName) {
      const input = document.getElementById("reportName");

      if (input) {
        input.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }
  }, [errors.reportName]);

  useEffect(() => {
    reset();
  }, [open]);

  // Resync default selection if context.subOrganizations resolves AFTER the
  // modal has already mounted (RHF Controller's defaultValue only applies
  // once at field registration, so a slow fetch would otherwise leave this
  // field permanently empty).
  useEffect(() => {
    if (open && context?.subOrganizations?.length) {
      const current = getValues("suborganizations");
      if (!current || current.length === 0) {
        setValue(
          "suborganizations",
          context.subOrganizations.map((e) => e.refId),
        );
      }
    }
  }, [open, context?.subOrganizations, getValues, setValue]);

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<img src="/img/export-white.svg" alt="" />}
        onClick={() => setOpen(true)}
      >
        {t("Generated-Report")}
      </Button>
      <CustomModal
        header={
          <Typography variant="h1" color={"var(--dark)"}>
            {t("Generated-Report")}
          </Typography>
        }
        body={
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box>
              <Grid2
                container
                rowGap={"20px"}
                columnSpacing={{ xs: "0px", md: "40px" }}
                sx={{
                  width: "100%",
                }}
              >
                <Grid2 xs={12}>
                  <FormatSelector
                    name={"formats"}
                    control={control}
                    label={t("Report-Format")}
                    required
                    rules={{
                      validate: (value: string[]) =>
                        (value && value.length > 0) ||
                        t("This-field-is-required", {
                          param: t("Report-Format"),
                        }),
                    }}
                    options={[
                      {
                        value: "excel",
                        label: t("Excel"),
                        subtitle: ".xlsx",
                        color: "#217346",
                        icon: <GridOnIcon sx={{ fontSize: "28px" }} />,
                      },
                      {
                        value: "word",
                        label: t("Word"),
                        subtitle: ".docx",
                        color: "#2B579A",
                        icon: <DescriptionIcon sx={{ fontSize: "28px" }} />,
                      },
                    ]}
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <InputReactHookForm
                    required
                    label={t("Reporting-Name")}
                    info={t("Reporting-Name")}
                    placeholder={t("Reporting-Name")}
                    name="reportName"
                    control={control}
                    rules={{
                      required: t("This-field-is-required", {
                        param: t("Reporting-Name"),
                      }),
                    }}
                    defaultValue=""
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <MultipleAutoComplete
                    name={"suborganizations"}
                    control={control}
                    rules={{
                      required: t("This-field-is-required", {
                        param: t("Sub-Organizations"),
                      }),
                    }}
                    label={t("Sub-Organizations")}
                    info={t("Sub-Organizations")}
                    placeholder={t("Select-Param", {
                      param: t("Sub-Organizations"),
                    })}
                    listMenuItem={
                      context && context.subOrganizations
                        ? ArrayUtils.convertArrayToMenuData(
                            "refId",
                            "name",
                            "name",
                            context.subOrganizations,
                          )
                        : []
                    }
                    defaultValue={
                      context && context.subOrganizations
                        ? context?.subOrganizations?.map((e) => e.refId)
                        : []
                    }
                    storeValue
                    required
                  />
                  {/* <CustomAutoComplete
                    disabled
                    required
                    name={"templates"}
                    control={control}
                    rules={{}}
                    // rules={{
                    //   required: t("This-field-is-required", {
                    //     param: t("Template"),
                    //   }),
                    // }}
                    label={t("Template")}
                    info={t("New-Feature-In-The-Next-Version")}
                    placeholder={t("Select-Param", {
                      param: t("Template"),
                    })}
                    listMenuItem={
                      context && context.subOrganizations
                        ? context?.subOrganizations?.map((e) => ({
                            nameEN: e.name,
                            nameTH: e.name,
                            value: e.refId,
                          }))
                        : []
                    }
                    defaultValue={""}
                  /> */}
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <FormControl fullWidth>
                    <Controller
                      name="startDate"
                      control={control}
                      defaultValue={dayjs(`${dayjs().year()}-01-01`)}
                      rules={{
                        required: true,
                        onChange(event) {
                          setDefaultDate(event);
                        },
                      }}
                      render={({ field: { onChange, value, ref } }) => (
                        <CustomDatePicker
                          value={value}
                          label={t("Start-Date")}
                          info={t("Start-Date")}
                          required
                          handleOnChange={onChange}
                          defaultDate={value}
                          inputRef={ref}
                        />
                      )}
                    />
                  </FormControl>
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <FormControl fullWidth>
                    <Controller
                      name="endDate"
                      control={control}
                      defaultValue={dayjs(`${dayjs().year()}-12-01`)}
                      rules={{
                        required: true,
                      }}
                      render={({ field: { onChange, value, ref } }) => (
                        <CustomDatePicker
                          value={value}
                          label={t("End-Date")}
                          info={t("End-Date")}
                          required
                          handleOnChange={onChange}
                          defaultDate={value}
                          inputRef={ref}
                          minDate={startDate ?? dayjs()}
                          maxDate={
                            startDate
                              ? startDate.add(11, "M")
                              : dayjs(`${dayjs().year()}-12-01`)
                          }
                        />
                      )}
                    />
                  </FormControl>
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <FormControl fullWidth>
                    <Controller
                      name="startDateBaseYear"
                      control={control}
                      defaultValue={null}
                      rules={{
                        onChange(event) {
                          if (event.target.value) {
                            setValue(
                              "endDateBaseYear",
                              event.target.value.add(11, "M"),
                            );
                          }
                        },
                      }}
                      render={({ field: { onChange, value, ref } }) => (
                        <CustomDatePicker
                          value={value}
                          label={t("Start-Base-Year-Period")}
                          info={t("Start-Base-Year-Period")}
                          handleOnChange={onChange}
                          defaultDate={value}
                          inputRef={ref}
                          maxDate={startDate ? startDate : undefined}
                          clearable
                        />
                      )}
                    />
                  </FormControl>
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <FormControl fullWidth>
                    <Controller
                      name="endDateBaseYear"
                      control={control}
                      defaultValue={null}
                      render={({ field: { onChange, value, ref } }) => (
                        <CustomDatePicker
                          value={value}
                          label={t("End-Base-Year-Period")}
                          info={t("End-Base-Year-Period")}
                          handleOnChange={onChange}
                          defaultDate={value}
                          inputRef={ref}
                          minDate={startDateBaseYear ?? dayjs()}
                          maxDate={
                            startDateBaseYear
                              ? startDateBaseYear.add(11, "M")
                              : undefined
                          }
                          clearable
                        />
                      )}
                    />
                  </FormControl>
                </Grid2>
                <Grid2
                  xs={12}
                  md={6}
                  sx={{
                    ".MuiSelect-select": {
                      padding: "12px !important",
                    },
                  }}
                >
                  <SelectCheckmarks
                    name={"status"}
                    required
                    control={control}
                    label={t("Status")}
                    info={t("Status")}
                    placeholder={""}
                    defaultValue={["approved", "pending", "draft"]}
                    rules={{
                      required: t("This-field-is-required", {
                        param: t("Status"),
                      }),
                    }}
                    listMenuItem={[
                      {
                        nameEN: t("Approve"),
                        nameTH: t("Approve"),
                        value: "approved",
                      },
                      {
                        nameEN: t("Pending"),
                        nameTH: t("Pending"),
                        value: "pending",
                      },
                      {
                        nameEN: t("Draft"),
                        nameTH: t("Draft"),
                        value: "draft",
                      },
                    ]}
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <InputReactHookForm
                    label={t("Contact")}
                    info={t("Contact")}
                    placeholder={t("Contact")}
                    name="contact"
                    control={control}
                    defaultValue={context ? context.organization?.name : ""}
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <InputReactHookForm
                    label={t("Productivity")}
                    info={t("Productivity")}
                    placeholder={t("Productivity")}
                    name="productivity"
                    control={control}
                    defaultValue=""
                    rules={{
                      pattern: {
                        value: NUMBER_REGEX,
                        message: t("only-numbers-are-allowed"),
                      },
                    }}
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <InputReactHookForm
                    label={t("Base-Productivity")}
                    info={t("Base-Productivity")}
                    placeholder={t("Base-Productivity")}
                    name="baseProductivity"
                    control={control}
                    defaultValue=""
                    rules={{
                      pattern: {
                        value: NUMBER_REGEX,
                        message: t("only-numbers-are-allowed"),
                      },
                    }}
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <InputReactHookForm
                    label={t("Specify-Unit")}
                    info={t("Specify-Unit")}
                    placeholder={t("Specify-Unit-Placeholder")}
                    name="productivityUnit"
                    control={control}
                    defaultValue=""
                  />
                </Grid2>
                <Grid2
                  md={6}
                  sx={{
                    display: { xs: "none", md: "block" },
                  }}
                ></Grid2>
                <UploadImage
                  image={logo}
                  handleImageChange={(event) => handleLogoChange(event)}
                  headline={t("Organization-Logo")}
                  text={t("Organization-Logo-Description")}
                  deleteImage={() => setValue("logo", "")}
                />

                <UploadImage
                  image={map}
                  handleImageChange={(event) => handleMapChange(event)}
                  headline={t("Org-Map")}
                  text={t("Report-Upload-Image-Description")}
                  deleteImage={() => setValue("map", "")}
                />
                <UploadImage
                  image={chart}
                  handleImageChange={(event) => handleChartChange(event)}
                  headline={t("Org-Chart")}
                  text={t("Report-Upload-Image-Description")}
                  deleteImage={() => setValue("chart", "")}
                />
                {isWordSelected && (
                  <UploadImage
                    image={flowchart}
                    handleImageChange={(event) => handleFlowchartChange(event)}
                    headline={t("Production-Process-Chart")}
                    text={t("Report-Upload-Image-Description")}
                    deleteImage={() => setValue("flowchart", "")}
                  />
                )}
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="secondary"
                  style={{
                    marginTop: "20px",
                  }}
                >
                  {t("Generated-Report")}
                </Button>
              </Grid2>
            </Box>
          </form>
        }
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

interface FormatOption {
  value: ReportFormat;
  label: string;
  subtitle: string;
  color: string;
  icon: React.ReactNode;
}

interface FormatSelectorProps {
  name: "formats";
  control: Control<GenerateTGOReportForm>;
  label: string;
  required?: boolean;
  rules?: Omit<
    RegisterOptions<GenerateTGOReportForm, "formats">,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  options: FormatOption[];
}

const FormatSelector = (props: FormatSelectorProps) => {
  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={["excel"]}
      rules={props.rules}
      render={({ field: { value = [], onChange }, fieldState: { error } }) => {
        const toggle = (v: ReportFormat) => {
          const next = value.includes(v)
            ? value.filter((x: ReportFormat) => x !== v)
            : [...value, v];
          onChange(next);
        };

        return (
          <>
            <Box sx={{ marginBottom: "8px" }}>
              <LabelInput
                label={props.label}
                info={props.label}
                required={props.required}
              />
            </Box>
            <Box
              display={"flex"}
              gap={"16px"}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              {props.options.map((opt) => {
                const selected = value.includes(opt.value);
                return (
                  <Box
                    key={opt.value}
                    role="checkbox"
                    aria-checked={selected}
                    onClick={() => toggle(opt.value)}
                    sx={{
                      flex: 1,
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      padding: "18px 20px",
                      borderRadius: "14px",
                      cursor: "pointer",
                      userSelect: "none",
                      backgroundColor: selected
                        ? "rgba(11, 148, 133, 0.06)"
                        : "var(--white)",
                      border: selected
                        ? "1.5px solid var(--green)"
                        : "1.5px solid #E5E8EB",
                      boxShadow: selected
                        ? "0 6px 18px rgba(11, 148, 133, 0.16)"
                        : "0 1px 2px rgba(16, 24, 40, 0.04)",
                      transition: "all 0.18s ease",
                      "&:hover": {
                        borderColor: "var(--green)",
                        boxShadow: "0 6px 18px rgba(16, 24, 40, 0.10)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--white)",
                        backgroundColor: opt.color,
                      }}
                    >
                      {opt.icon}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h2"
                        color={"var(--dark)"}
                        sx={{ lineHeight: 1.2 }}
                      >
                        {opt.label}
                      </Typography>
                      <Typography
                        variant="text1"
                        color={"#8A9099"}
                        sx={{ display: "block" }}
                      >
                        {opt.subtitle}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        flexShrink: 0,
                        width: "22px",
                        height: "22px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {selected ? (
                        <CheckCircleIcon
                          sx={{ fontSize: "22px", color: "var(--green)" }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            border: "2px solid #CDD2D8",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {error && (
              <Typography
                variant="text1"
                color={"var(--red)"}
                sx={{ display: "block", marginTop: "8px" }}
              >
                {error.message}
              </Typography>
            )}
          </>
        );
      }}
    />
  );
};

interface UploadImageProps {
  image: string;
  headline: string;
  text: string;
  handleImageChange: (event: any) => Promise<void>;
  deleteImage: () => void;
}

const UploadImage = (props: UploadImageProps) => {
  const iconStyle: CSSProperties = {
    color: "white",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  };
  const { t } = useTranslation([common, report], {
    nsMode: "fallback",
  });

  const viewImage = () => {
    try {
      const file: File = FileUtils.base64ToFile(props.image);
      window.open(URL.createObjectURL(file));
    } catch (error) {}
  };

  return (
    <Grid2 xs={12} md={6}>
      <LabelInput label={props.headline} info={t(props.text)} />
      <Box
        gap={"20px"}
        display={"flex"}
        flexDirection={{ xs: "column", sm: "row" }}
        textAlign={{ xs: "center", sm: "start" }}
        marginTop={"8px"}
      >
        <Button
          component="label"
          variant="outlined"
          sx={{
            padding: "8px",
            border: "1px dashed #818181",
            borderRadius: "8px",
            width: "112px",
            minWidth: "112px",
            height: "112px",
          }}
        >
          <Avatar
            variant="square"
            sx={{
              fontSize: "56px",
              backgroundColor: "transparent",
              width: "100%",
              height: "100%",
            }}
          >
            {props.image && props.image !== "" ? (
              <img
                src={`${props.image}`}
                alt="resized"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                key={props.headline}
                id={props.headline}
              />
            ) : (
              <AddPhotoAlternateIcon fontSize={"inherit"} color="primary" />
            )}
          </Avatar>

          <input
            type="file"
            accept="image/*"
            onChange={props.handleImageChange}
            hidden
            key={props.headline}
            id={props.headline}
            multiple={false}
          />
        </Button>

        <Stack gap={"10px"} justifyContent={"center"} flex={1} height={"112px"}>
          <Button
            variant="contained"
            color={"secondary"}
            component="label"
            startIcon={<img src="/img/camera.svg" alt="" />}
          >
            {t("Upload")}

            <input
              type="file"
              accept="image/*"
              onChange={props.handleImageChange}
              hidden
              key={props.headline + "btn"}
              id={props.headline + "btn"}
              multiple={false}
            />
          </Button>

          {!props.image || props.image === "" ? (
            <Typography variant="text1" color={"var(--dark)"} height={"48px"}>
              {`${t("Upload-picture")} ${t("Upload-max-size")} ${
                MAX_UPLOAD_FILE_SIZE_IMPORT / 1e6
              } MB`}
            </Typography>
          ) : null}

          <Box
            display={
              props.image !== undefined && props.image !== "" ? "flex" : "none"
            }
            gap={"12px"}
            height={"48px"}
          >
            <IconButton
              type="button"
              onClick={viewImage}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "var(--green)",
              }}
            >
              <RemoveRedEyeIcon style={iconStyle} />
            </IconButton>

            <IconButton
              type="button"
              onClick={props.deleteImage}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "var(--red)",
              }}
            >
              <DeleteForeverIcon style={iconStyle} />
            </IconButton>
          </Box>
        </Stack>
      </Box>
    </Grid2>
  );
};
